import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { INSIGHTS_BACKEND_URL, NEST_BACKEND_URL } from "@/lib/axios";
import { QueryConfig } from "@/lib/react-query";
import { getMappedChannelNames } from "@/shared/helpers/getMappedChannelNames";
import { useModuleEndpoint } from "@/shared/hooks/useModuleEndpoint";
import { ChannelDataWithSourceAndTotal, MetricType } from "@/shared/types";
import { axiosApi } from "@/Utils";

const getGeographiesROIsV2 = ({
  brand,
  category,
  product,
  channels,
  startDate,
  endDate,
  geographies = [],
  graphView,
  tableView,
  businessUnitToggle,
  metricType,
}: {
  brand: string;
  category: string;
  product: string;
  channels: string[];
  graphView: "channel" | "insight";
  tableView: "channel" | "insight";
  businessUnitToggle: "aggregate" | "split";
  metricType: MetricType;
  startDate?: string | undefined;
  endDate?: string | undefined;
  geographies?: string[] | undefined;
}): Promise<{
  chartView: ChannelDataWithSourceAndTotal[];
  tableView: ChannelDataWithSourceAndTotal[];
  totals: {
    totalSpend: number;
    totalRevenue: number;
    totalRoi: number;
  };
}> => {
  const payload: Record<string, any> = {
    brand,
    category,
    product,
    channels,
    startDate,
    endDate,
    geographies,
    graphView,
    tableView,
    businessUnitToggle,
    metricType,
  };

  return axiosApi.get("/api/v2/geographies", { params: payload, baseURL: INSIGHTS_BACKEND_URL });
};

const _getGeographiesROIsNest = async ({
  brand,
  category,
  product,
  channels,
  startDate,
  endDate,
  geographies = [],
  graphView,
  tableView,
  businessUnitToggle,
}: {
  brand: string;
  category: string;
  product: string;
  channels: string[];
  graphView: "channel" | "insight";
  tableView: "channel" | "insight";
  businessUnitToggle: "aggregate" | "split";
  metricType: MetricType;
  startDate?: string | undefined;
  endDate?: string | undefined;
  geographies?: string[] | undefined;
}): Promise<{
  chartView: ChannelDataWithSourceAndTotal[];
  tableView: ChannelDataWithSourceAndTotal[];
  totals: {
    totalSpend: number;
    totalRevenue: number;
    totalRoi: number;
  };
}> => {
  const params = new URLSearchParams();

  if (brand) params.append("brand", brand);
  if (category) params.append("category", category);
  if (product) params.append("product", product);
  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);
  if (graphView) params.append("graphView", graphView);
  if (tableView) params.append("tableView", tableView);
  if (businessUnitToggle) params.append("businessUnitToggle", businessUnitToggle);

  if (channels && channels.length > 0) {
    channels.forEach((channel) => {
      params.append("channels", channel);
    });
  }

  if (geographies && geographies.length > 0) {
    geographies.forEach((geography) => {
      params.append("geographies", geography);
    });
  }

  const result = await axiosApi.get(`/api/geography?${params.toString()}`, {
    baseURL: NEST_BACKEND_URL,
  });

  return result.data;
};

export const useGeographiesROIs = ({
  brand,
  category,
  product,
  channels,
  startDate,
  endDate,
  geographies = [],
  graphView,
  tableView,
  businessUnitToggle,
  metricType,
  config,
}: {
  brand: string;
  category: string;
  product: string;
  channels: string[];
  startDate: string;
  endDate: string;
  graphView: "channel" | "insight";
  tableView: "channel" | "insight";
  businessUnitToggle: "aggregate" | "split";
  metricType: MetricType;
  geographies?: string[];
  config?: QueryConfig<typeof getGeographiesROIsV2 | typeof _getGeographiesROIsNest>;
}) => {
  const endpointType = useModuleEndpoint("geographies");

  const queryKey = useMemo(
    () => [
      "geographies-roi-v3",
      brand,
      category,
      product,
      channels,
      startDate,
      endDate,
      geographies,
      graphView,
      tableView,
      businessUnitToggle,
      metricType,
      endpointType,
    ],
    [
      brand,
      category,
      product,
      channels,
      startDate,
      endDate,
      geographies,
      graphView,
      tableView,
      businessUnitToggle,
      metricType,
      endpointType,
    ]
  );

  const queryFn = useCallback(() => {
    if (endpointType === "new") {
      return _getGeographiesROIsNest({
        brand,
        category,
        product,
        channels,
        startDate,
        endDate,
        geographies,
        graphView,
        tableView,
        businessUnitToggle,
        metricType,
      });
    } else {
      return getGeographiesROIsV2({
        brand,
        category,
        product,
        channels,
        startDate,
        endDate,
        geographies,
        graphView,
        tableView,
        businessUnitToggle,
        metricType,
      });
    }
  }, [
    brand,
    category,
    product,
    channels,
    startDate,
    endDate,
    geographies,
    graphView,
    tableView,
    businessUnitToggle,
    metricType,
    endpointType,
  ]);

  return useQuery({
    ...config,
    queryKey,
    queryFn,
    select: (data) => ({
      ...data,
      tableView: getMappedChannelNames(data.tableView),
      chartView: getMappedChannelNames(data.chartView),
    }),
  });
};
